/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Router } from '@angular/router';
import { InvoiceService } from '../../services/invoice.service';
import { TemplateService } from '../../services/template.service';
import { CompanyClient, CurrencyPipe, PremiumModalComponent } from '../../../shared';
import { COUNTRY_TO_CURRENCY_MAP } from '../../../shared/constants/country-currency.constant';
import { InvoicePreviewService } from '../../services/invoice-preview.service';
import { InvoicePayload, Template } from '../../models';
import { LoginModalComponent } from '../../../auth/modals/login-modal/login-modal.component';
import { AuthService } from '../../../auth/services';
import { SubscriptionService, UserService } from '../../../dashboard/services';
import { CompanyService } from '../../../dashboard/services/company.service';
import { ClientService } from '../../../dashboard/services/client.service';
import { of, switchMap, take } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { User } from '../../../dashboard/models';
import { InvoiceFormCompanyComponent } from './invoice-form-company/invoice-form-company.component';
import { InvoiceFormClientComponent } from './invoice-form-client/invoice-form-client.component';

declare let bootstrap: any;

@Component({
  selector: 'kt-app-invoice-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CurrencyPipe,
    LoginModalComponent,
    InvoiceFormCompanyComponent,
    InvoiceFormClientComponent,
    PremiumModalComponent
],
  templateUrl: './invoice-form.component.html',
  styleUrls: ['./invoice-form.component.scss']
})
export class InvoiceFormComponent implements OnInit, AfterViewInit  {
  fb = inject(FormBuilder);
  invoiceService = inject(InvoiceService);
  templateService = inject(TemplateService);
  invoicePreviewService = inject(InvoicePreviewService);
  router = inject(Router);

  authService = inject(AuthService);
  userService = inject(UserService);
  companyService = inject(CompanyService);
  clientService = inject(ClientService);
  subscriptionService = inject(SubscriptionService);

  currentUser: User | null = null;
  companies: CompanyClient[] = [];
  clients: CompanyClient[] = [];

  @ViewChild('loginModal') loginModal!: LoginModalComponent;

  private pendingSubmit = false;

  selectedTemplate: Template | null = null;

  invoiceForm!: FormGroup;
  
  // Toggle flags for financial inputs
  enableGSTHSTInputs = false;
  showTaxInput = true;
  showDiscountInput = false;
  showShippingInput = false;

  // Financial properties
  discountAmount = 0;
  shippingAmount = 0;
  taxPercentage = 13;

  defaultCountry = 'Canada';
  defaultCurrency = 'CAD';

  // Country to Currency mapping
  countryToCurrencyMap: Record<string, string> = COUNTRY_TO_CURRENCY_MAP;

  ngOnInit(): void {
    this.initializeForm();

    if (!this.loadSelectedTemplate()) {
      return;
    }

    this.checkAuthAndMaybeOpenModal();

    this.setFormControlState(['gstNumber', 'hstNumber'], this.enableGSTHSTInputs, Validators.required);
  }

  ngAfterViewInit(): void {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach(el => {
      new bootstrap.Tooltip(el);
    });
  }

  loadSelectedTemplate(): boolean {
    this.selectedTemplate = this.templateService.getSelectedTemplateValue();

    if (!this.selectedTemplate) {
      this.router.navigate(['/invoice']);
      return false;
    }

    return true;
  }

  checkAuthAndMaybeOpenModal(): void {
    if (!this.authService.isLoggedIn()) {
      this.pendingSubmit = true;

      // Defer to avoid ExpressionChanged errors
      setTimeout(() => {
        this.loginModal?.open();
      });
    }else{
      this.currentUser = this.userService.currentUser();
      if (this.currentUser?.id) {
        this.loadUserCompaniesAndClients();
      }
    }
  }

  loadUserCompaniesAndClients(): void {
    if (!this.currentUser?.id) {
      return;
    }

    const userId = this.currentUser.id;

    // Load companies
    this.companyService.getCompanies(userId).subscribe({
      next: (companies) => {
        this.companies = companies;
      },
      error: (err) => {
        console.error('Failed to load companies:', err);
      }
    });

    // Load clients
    this.clientService.getClients(userId).subscribe({
      next: (clients) => {
        this.clients = clients;
      },
      error: (err) => {
        console.error('Failed to load clients:', err);
      }
    });
  }

  initializeForm(): void {
    this.invoiceForm = this.fb.group({
      // Company Details
      companyName: ['', Validators.required],
      companyAddress: ['', Validators.required],
      companyCity: ['', Validators.required],
      companyProvince: ['', Validators.required],
      companyPostalCode: ['', Validators.required],
      companyCountry: [this.defaultCountry, Validators.required],
      companyPhone: [''],
      companyLogo: [''],
      companyCurrency: [this.defaultCurrency, Validators.required],
      
      // Business Details
      taxEnabled: [false],
      gstNumber: [{value: '', disabled: this.enableGSTHSTInputs}],
      hstNumber: [''],
      websiteLink: [''],
      
      // Client Details
      clientName: ['', Validators.required],
      clientAddress: ['', Validators.required],
      clientCity: ['', Validators.required],
      clientProvince: ['', Validators.required],
      clientPostalCode: ['', Validators.required],
      clientCountry: [this.defaultCountry, Validators.required],
      saveClientData: [true],
      
      // Invoice Details
      //invoiceNumber: ['INV-' + Date.now(), Validators.required],
      invoiceNumber: [this.invoiceService.getLastInvoiceNumber() ?? '01', Validators.required],
      invoiceDate: [new Date().toISOString().split('T')[0], Validators.required],
      dueDate: ['', Validators.required],

      // Invoice Items (new FormArray)
      items: this.fb.array([
        this.createItem()
      ], Validators.required),
      
      // Financial Details
      taxPercentage: [this.taxPercentage],
      discountAmount: [this.discountAmount],
      shippingAmount: [this.shippingAmount],
      
      // Additional Info
      notes: ['']
    });
  }

  get companyCurrency(): string {
    return this.invoiceForm.get('companyCurrency')?.value || this.defaultCurrency;
  }

  /** Getter for easy access to FormArray */
  get items(): FormArray {
    return this.invoiceForm.get('items') as FormArray;
  }

  /** Create one invoice item FormGroup */
  createItem(): FormGroup {
    return this.fb.group({
      description: ['', Validators.required],
      quantityOrHours: [1, [Validators.required, Validators.min(1)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      total: [{ value: 0, disabled: true }]
    });
  }

  /** Add new item */
  addItem(): void {
    this.items.push(this.createItem());
  }

  /** Remove item */
  removeItem(index: number): void {
    this.items.removeAt(index);
  }

  /** Calculate total for a specific item */
  calculateItemTotal(index: number): void {
    const item = this.items.at(index) as FormGroup;
    const qty = item.get('quantityOrHours')!.value;
    const price = item.get('unitPrice')!.value;
    item.get('total')!.setValue(qty * price, { emitEvent: false });
  }

  toggleTaxInput(): void {
    this.showTaxInput = !this.showTaxInput;

    this.setFormControlState(['taxPercentage'], this.showTaxInput, Validators.required);
  }

  toggleDiscountInput(): void {
    this.showDiscountInput = !this.showDiscountInput;

    this.setFormControlState(['discountAmount'], this.showDiscountInput, Validators.required);
  }

  toggleShippingInput(): void {
    this.showShippingInput = !this.showShippingInput;

    this.setFormControlState(['shippingAmount'], this.showShippingInput, Validators.required);
  }

  /**
 * Enable/disable form controls and apply/remove validators
 * @param controlNames - array of form control names
 * @param enabled - true to enable, false to disable
 * @param validator - validator to apply when enabled
 */
private setFormControlState(controlNames: string[], enabled: boolean, validator: any): void {
  controlNames.forEach(name => {
    const ctrl = this.invoiceForm.get(name);
    if (!ctrl) return;

    if (enabled) {
      ctrl.setValidators([validator]);
      ctrl.enable();
    } else {
      ctrl.clearValidators();
      ctrl.disable();
    }

    ctrl.updateValueAndValidity();
  });
}

  onCompanyCountryChange(selectedCountry: string): void {
    const currency = this.countryToCurrencyMap[selectedCountry];
    if (currency) {
      this.invoiceForm.patchValue({ companyCurrency: currency });
    }
  }

  get subtotal(): number {
    return this.items.controls.reduce((sum, control) => {
      const group = control as FormGroup;
      const total = group.get('total')?.value || 0;
      return sum + total;
    }, 0);
  }


  get tax(): number {
    const taxRate = this.invoiceForm.get('taxPercentage')?.value || this.taxPercentage;
    return Math.round((this.subtotal - this.discountAmount) * (taxRate / 100) * 100) / 100;
  }

  get total(): number {
    this.discountAmount = this.invoiceForm.get('discountAmount')?.value || this.discountAmount;
    this.shippingAmount = this.invoiceForm.get('shippingAmount')?.value || this.shippingAmount;
    return this.subtotal - this.discountAmount + this.shippingAmount + this.tax;
  }

  private mapInvoiceToPayload(formValue: any): InvoicePayload {
    const formattedCompanyName = (formValue.companyName ?? '')
      .replace(/\s+/g, '_')
      .replace(/\./g, '');
    return {
      userId: this.currentUser!.id,
      invoiceMetadata: {
        invoiceNumber: formValue.invoiceNumber,
        invoiceDate: formValue.invoiceDate,
        invoiceDueDate: formValue.dueDate,
        invoiceItems: this.invoiceForm.get('items')?.value || [],
        notes: formValue.notes
      },
      companyInfo: {
        userId: this.currentUser!.id,
        name: formValue.companyName,
        address: {
          address: formValue.companyAddress,
          city: formValue.companyCity,
          province: formValue.companyProvince,
          postalCode: formValue.companyPostalCode,
          country: formValue.companyCountry,
          countryCurrency: this.defaultCurrency
        },
        email: '',
        phone: formValue.companyPhone,
        gstNumber: formValue.gstNumber,
        hstNumber: formValue.hstNumber,
        websiteLink: formValue.websiteLink,
        logoId: undefined
      },
      clientInfo: {
        userId: this.currentUser!.id,
        name: formValue.clientName,
        address: {
          address: formValue.clientAddress,
          city: formValue.clientCity,
          province: formValue.clientProvince,
          postalCode: formValue.clientPostalCode,
          country: formValue.clientCountry,
          countryCurrency: this.defaultCurrency
        },
        email: undefined,
        phone: undefined,
        logoId: undefined
      },
      financialDetails: {
        taxPercentage: formValue.taxPercentage || this.taxPercentage,
        discountAmount: formValue.discountAmount || this.discountAmount,
        shippingAmount: formValue.shippingAmount || this.shippingAmount,
        subtotal: this.subtotal,
        taxAmount: this.tax,
        totalAmount: this.total
      },
      appearance: {
        pdfFileName: `${formattedCompanyName}_Invoice_${formValue.invoiceNumber}`,
        defaultColor: this.selectedTemplate?.defaultColor || '#4CAF50',
        defaultColorLight: this.selectedTemplate?.defaultColorLight || '#E8F5E9'
      },
      height: 1000,
      width: 800
    };
  }

  resetForm(): void {
    this.invoiceForm.reset({
      invoiceNumber: this.invoiceService.getLastInvoiceNumber() ?? '01',
      invoiceDate: new Date().toISOString().split('T')[0],
      companyCountry: this.defaultCountry,
      companyCurrency: this.defaultCurrency,
      clientCountry: this.defaultCountry,
      saveClientData: true,
      items: [
        {
          description: '',
          quantityOrHours: 1,
          unitPrice: 0,
          total: 0
        }
      ],
      taxPercentage: this.taxPercentage,
      discountAmount: this.discountAmount,
      shippingAmount: this.shippingAmount
    });
    this.showTaxInput = true;
    this.showDiscountInput = false;
    this.showShippingInput = false;
  }

  onSubmit(): void {
    if (this.invoiceForm.invalid || this.items.length === 0) {
      this.invoiceForm.markAllAsTouched();
      return;
    }

    if (!this.authService.isLoggedIn()) {
      this.pendingSubmit = true;
      this.loginModal.open();
      return;
    }

    const formValue = this.invoiceForm.value;

    // Map form to InvoicePayload before sending to endpoint
    const invoicePayload: InvoicePayload = this.mapInvoiceToPayload(formValue);

    this.invoiceService.createInvoice(invoicePayload).pipe(
      switchMap((createdInvoice) => {
        if (createdInvoice?.id) {
          return this.invoiceService.getInvoicePdfById(createdInvoice.id);
        }
        return of(undefined);
      }),
      take(1)
    ).subscribe({
      next: (response: HttpResponse<Blob> | undefined) => {
        
        // this.addCompayDetails();
        // if (this.invoiceForm.value.saveClientData) {
        //   this.addClientDetails();
        // }

        if (response?.body) {
          const contentDisposition = response.headers.get('Content-Disposition') || '';
          const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
          const pdfName = matches ? matches[1] : 'invoice.pdf';
          this.invoicePreviewService.setPdfBlob({
            blob: response.body,
            filename: pdfName
          });
          this.router.navigate(['/invoice/preview']);
        }
      },
      error: (err) => {
        console.error('Failed to create invoice or get PDF:', err);
      }
    });
  }

  onLoginSuccess(): void {
    if (this.pendingSubmit) {
      this.pendingSubmit = false;
      //this.createInvoice();
    }
  }

  clearZero(controlName: string) {
    const control = this.invoiceForm.get(controlName);
    if (control?.value === 0) {
      control.setValue(null);
    }
  }

  clearZeroInItem(index: number, controlName: string) {
    const control = this.items.at(index).get(controlName);
    if (control?.value === 0) {
      control.setValue(null);
    }
  }

  handleOpenPremiumModal(event: { type: 'client' | 'company'; count: number }) {
    this.subscriptionService.checkAndShowPremium(event.type, event.count);
  }
}
